import type { QueryClient } from '@tanstack/react-query'

/**
 * Boundary between the two foreground-refresh tiers. Below this the socket
 * almost certainly survived (iOS keeps the process alive briefly; browsers
 * don't freeze a tab that fast), so only STALE queries refresh — enough to
 * update a home-screen list that's past its staleTime without refetching the
 * whole game screen on every app-switcher flick. Above it, assume the socket
 * may have died and force-refresh everything.
 */
export const MIN_BACKGROUND_MS = 15_000

/** What a foreground transition should refresh. */
export type ForegroundRefresh = 'all' | 'stale' | 'none'

/**
 * Refresh queries currently rendered by the app.
 *
 * 'all' intentionally refetches fresh queries too: a notification can be the
 * user's first indication that server state changed while this process/tab
 * was inactive, so waiting for staleTime would leave the just-opened screen
 * stale. 'stale' respects staleTime — the cheap tier for quick flicks.
 */
export function refetchActiveQueries(
  queryClient: Pick<QueryClient, 'refetchQueries'>,
  mode: 'all' | 'stale' = 'all',
): void {
  void queryClient.refetchQueries(
    mode === 'stale' ? { type: 'active', stale: true } : { type: 'active' },
  )
}

/** Return whether the app is currently visible/foregrounded in its platform. */
export function isAppActive(
  platform: 'web' | 'native',
  visibilityState: DocumentVisibilityState | undefined,
  appState: string | null | undefined,
): boolean {
  return platform === 'web' ? visibilityState === 'visible' : appState === 'active'
}

/** Foreground/background tracking state for the lifecycle refresh. */
export interface ForegroundTracker {
  active: boolean
  /** Epoch-ms when the app last left the foreground; null while active, or if
   *  the app has never been seen active (unknown background start). */
  backgroundedAt: number | null
}

/**
 * Advance the tracker for a platform activity report and decide what the
 * transition should refresh.
 *
 * On a background → active transition: 'all' after at least `minBackgroundMs`
 * away or an unknown background start (the app was never seen active — a
 * conservative full refetch is harmless there); 'stale' for shorter stays, so
 * lists past their staleTime (e.g. the home screen's games) still update on a
 * quick flick without refetching fresh data. Same-state reports never refresh:
 * the browser emits both `focus` and `visibilitychange` for one tab switch,
 * and repeated background reports must preserve the original backgroundedAt.
 */
export function trackAppActivity(
  prev: ForegroundTracker,
  isActive: boolean,
  now: number,
  minBackgroundMs: number = MIN_BACKGROUND_MS,
): { next: ForegroundTracker; refresh: ForegroundRefresh } {
  if (isActive === prev.active) return { next: prev, refresh: 'none' }
  if (!isActive) return { next: { active: false, backgroundedAt: now }, refresh: 'none' }
  const refresh: ForegroundRefresh =
    prev.backgroundedAt === null || now - prev.backgroundedAt >= minBackgroundMs
      ? 'all'
      : 'stale'
  return { next: { active: true, backgroundedAt: null }, refresh }
}
